import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';

const FeedbackPanel = () => {
  const { user } = useUser();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [role, setRole] = useState('Content Creator');
  const [submitted, setSubmitted] = useState(false);
  const [existingStatus, setExistingStatus] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if user already submitted
    const checkExisting = async () => {
      try {
        const res = await fetch('/api/reviews/check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: localStorage.getItem('auth_token') })
        });
        const data = await res.json();
        if (data.hasReview) {
          setSubmitted(true);
          setExistingStatus(data.status);
        }
      } catch (e) {}
    };
    checkExisting();
  }, []);

  const handleSubmit = async () => {
    if (rating === 0) { setError('Please select a star rating'); return; }
    if (reviewText.length < 10) { setError('Please write at least 10 characters'); return; }
    if (reviewText.length > 500) { setError('Review must be under 500 characters'); return; }

    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/reviews/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: localStorage.getItem('auth_token'),
          rating,
          text: reviewText,
          role
        })
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        setExistingStatus('pending');
      } else {
        setError(data.error || 'Failed to submit');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div style={{
        background: 'rgba(99,102,241,0.08)',
        border: '1px solid rgba(99,102,241,0.2)',
        borderRadius: '16px',
        padding: '32px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '12px' }}>
          {existingStatus === 'approved' ? '🎉' : existingStatus === 'rejected' ? '😔' : '⏳'}
        </div>
        <h3 style={{ color: '#ffffff', fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>
          {existingStatus === 'approved' 
            ? 'Your Review is Live!' 
            : existingStatus === 'rejected' 
              ? 'Review Not Published'
              : 'Thank You For Your Feedback!'}
        </h3>
        <p style={{ color: '#94a3b8', fontSize: '14px' }}>
          {existingStatus === 'approved' 
            ? 'Your review is now visible on our pricing page. Thanks for the support! 💜' 
            : existingStatus === 'rejected' 
              ? 'Unfortunately your review was not approved. Please contact support for details.'
              : 'Your review is pending approval and will appear on the site shortly.'}
        </p>
      </div>
    );
  }

  return (
    <div style={{
      background: 'rgba(99,102,241,0.08)',
      border: '1px solid rgba(99,102,241,0.2)',
      borderRadius: '16px',
      padding: '28px'
    }}>
      <h3 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>
        ⭐ Rate Your Experience
      </h3>
      <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '20px' }}>
        Your feedback helps other creators discover VlogClip AI
      </p>

      {/* Star Rating */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '32px',
                transition: 'transform 0.15s ease',
                transform: (hoverRating || rating) >= star ? 'scale(1.15)' : 'scale(1)',
                filter: (hoverRating || rating) >= star ? 'none' : 'grayscale(1) opacity(0.4)'
              }}
            >
              ⭐
            </button>
          ))}
          {rating > 0 && (
            <span style={{ 
              color: rating >= 4 ? '#34d399' : rating >= 3 ? '#fbbf24' : '#ef4444', 
              fontSize: '14px', 
              fontWeight: '600',
              alignSelf: 'center',
              marginLeft: '8px'
            }}>
              {rating === 5 ? 'Amazing!' : rating === 4 ? 'Great!' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : 'Poor'}
            </span>
          )}
        </div>
      </div>

      {/* Role Selector */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
          What best describes you?
        </label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 14px',
            background: 'rgba(15,23,42,0.6)',
            border: '1px solid rgba(99,102,241,0.2)',
            borderRadius: '10px',
            color: '#e2e8f0',
            fontSize: '14px',
            outline: 'none'
          }}
        >
          <option value="Content Creator">Content Creator</option>
          <option value="YouTuber">YouTuber</option>
          <option value="Podcast Host">Podcast Host</option>
          <option value="Social Media Manager">Social Media Manager</option>
          <option value="Digital Marketer">Digital Marketer</option>
          <option value="Freelance Creator">Freelance Creator</option>
          <option value="Agency Owner">Agency Owner</option>
          <option value="Educator">Educator</option>
          <option value="Small Business Owner">Small Business Owner</option>
          <option value="Influencer">Influencer</option>
          <option value="Filmmaker">Filmmaker</option>
          <option value="Other">Other</option>
        </select>
      </div>

      {/* Review Text */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
          Share your experience
        </label>
        <textarea
          value={reviewText}
          onChange={(e) => { setReviewText(e.target.value); setError(''); }}
          placeholder="What do you love about VlogClip AI? How has it helped your content creation?"
          maxLength={500}
          rows={4}
          style={{
            width: '100%',
            padding: '12px 14px',
            background: 'rgba(15,23,42,0.6)',
            border: '1px solid rgba(99,102,241,0.2)',
            borderRadius: '10px',
            color: '#e2e8f0',
            fontSize: '14px',
            resize: 'vertical',
            outline: 'none',
            fontFamily: 'inherit',
            lineHeight: '1.5',
            boxSizing: 'border-box'
          }}
        />
        <div style={{ 
          color: reviewText.length > 450 ? '#fbbf24' : '#64748b', 
          fontSize: '12px', 
          textAlign: 'right',
          marginTop: '4px'
        }}>
          {reviewText.length}/500
        </div>
      </div>

      {error && (
        <div style={{ 
          color: '#ef4444', 
          fontSize: '13px', 
          marginBottom: '12px',
          padding: '8px 12px',
          background: 'rgba(239,68,68,0.1)',
          borderRadius: '8px'
        }}>
          {error}
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={loading}
        style={{
          width: '100%',
          padding: '14px',
          background: loading ? '#4b5563' : 'linear-gradient(135deg, #667eea, #764ba2)',
          color: '#ffffff',
          border: 'none',
          borderRadius: '12px',
          fontSize: '15px',
          fontWeight: '700',
          cursor: loading ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease'
        }}
      >
        {loading ? 'Submitting...' : '✨ Submit Review'}
      </button>

      <p style={{ color: '#64748b', fontSize: '11px', textAlign: 'center', marginTop: '10px' }}>
        Reviews are moderated before appearing on the site
      </p>
    </div>
  );
};

export default FeedbackPanel;
